import { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3, CalendarIcon, Check, ChevronDown, LayoutGrid, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Calendar } from '@/components/ui/Calendar';
import { PerformanceMetricsChart } from '@/components/performance/PerformanceMetricsChart';
import { PerformanceTableView } from '@/components/performance/PerformanceTableView';
import { useCampaignPerformanceSeries } from '@/hooks/useCampaignPerformanceSeries';
import { formatDateRangeLabel, isoToLocalDate, localDateToIso } from '@/utils/dateRangePicker';
import {
  DEFAULT_PERFORMANCE_METRIC_IDS,
  PERFORMANCE_METRIC_DEFS,
  type PerformanceGranularity,
  type PerformanceMetricId,
  type PerformanceViewMode,
} from '@/utils/performanceChartMetrics';
import { getDefaultDashboardDateRange, type DateRange } from '@/api/reportDateRanges';
import type { DateRange as DayPickerDateRange } from 'react-day-picker';

const GRANULARITY_OPTIONS: Array<{ value: PerformanceGranularity; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

function appliedToDraft(startDate: string, endDate: string): DayPickerDateRange | undefined {
  const from = isoToLocalDate(startDate);
  const to = isoToLocalDate(endDate);
  if (!from) return undefined;
  return { from, to: to ?? from };
}

export type CampaignPerformancePanelProps = {
  profileId: string;
  campaignId: string;
  campaignName: string;
  layout?: 'embedded' | 'dialog';
};

export function CampaignPerformancePanel({
  profileId,
  campaignId,
  campaignName,
  layout = 'embedded',
}: CampaignPerformancePanelProps) {
  const defaultRange = useMemo(() => getDefaultDashboardDateRange(), []);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [granularity, setGranularity] = useState<PerformanceGranularity>('daily');
  const [viewMode, setViewMode] = useState<PerformanceViewMode>('graph');
  const [visibleMetricIds, setVisibleMetricIds] = useState<PerformanceMetricId[]>(
    DEFAULT_PERFORMANCE_METRIC_IDS
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [draftDateRange, setDraftDateRange] = useState<DayPickerDateRange | undefined>();
  const [metricsOpen, setMetricsOpen] = useState(false);
  const metricsRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isFetching, error, refetch } = useCampaignPerformanceSeries({
    profileId,
    campaignId,
    range: dateRange,
    granularity,
    enabled: Boolean(profileId) && Boolean(campaignId),
  });

  const points = data?.points ?? [];
  const hasData = data?.hasData ?? false;
  const loading = isLoading || isFetching;
  const rangeLabel = formatDateRangeLabel(dateRange.startDate, dateRange.endDate);

  const handleDatePickerOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftDateRange(appliedToDraft(dateRange.startDate, dateRange.endDate));
    }
    setDatePickerOpen(nextOpen);
  };

  const draftStart = draftDateRange?.from ? localDateToIso(draftDateRange.from) : '';
  const draftEnd = draftDateRange?.to
    ? localDateToIso(draftDateRange.to)
    : draftDateRange?.from
      ? localDateToIso(draftDateRange.from)
      : '';
  const isDraftInvalid = Boolean(draftStart && draftEnd && draftEnd < draftStart);
  const canApplyDateRange = Boolean(draftDateRange?.from && draftDateRange?.to && !isDraftInvalid);

  const handleDateRangeApply = () => {
    if (!canApplyDateRange || !draftDateRange?.from || !draftDateRange?.to) return;
    setDateRange({
      startDate: localDateToIso(draftDateRange.from),
      endDate: localDateToIso(draftDateRange.to),
    });
    setDatePickerOpen(false);
  };

  const toggleMetric = (metricId: PerformanceMetricId) => {
    setVisibleMetricIds((prev) => {
      if (prev.includes(metricId)) {
        if (prev.length === 1) return prev;
        return prev.filter((id) => id !== metricId);
      }
      return [...prev, metricId];
    });
  };

  useEffect(() => {
    if (!metricsOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (metricsRef.current && !metricsRef.current.contains(event.target as Node)) {
        setMetricsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [metricsOpen]);

  const title =
    layout === 'dialog' ? campaignName : 'Campaign Performance';
  const subtitle =
    layout === 'dialog'
      ? `1 selected • ${dateRange.startDate} to ${dateRange.endDate}`
      : `${campaignName} • ${rangeLabel}`;

  return (
    <div className="flex flex-col gap-4">
      <div
        className={
          layout === 'dialog'
            ? 'flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:pr-12'
            : 'flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between'
        }
      >
        <div className={layout === 'dialog' ? 'min-w-0 pr-12' : 'min-w-0 pr-8'}>
          <h2
            id={layout === 'dialog' ? 'campaign-performance-dialog-title' : undefined}
            className={
              layout === 'dialog'
                ? 'text-lg font-semibold text-foreground'
                : 'text-base font-semibold text-foreground'
            }
          >
            {title}
          </h2>
          <p className="mt-0.5 truncate text-xs text-foreground-subtle">{subtitle}</p>
        </div>

        <div
          className={
            layout === 'embedded'
              ? 'flex shrink-0 flex-nowrap items-center gap-2'
              : 'flex flex-wrap items-center gap-2'
          }
        >
          <Popover open={datePickerOpen} onOpenChange={handleDatePickerOpenChange}>
            <PopoverTrigger asChild>
              <Button type="button" variant="secondary" size="sm" className="gap-2 font-normal">
                <CalendarIcon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="max-w-[min(280px,50vw)] truncate text-left">{rangeLabel}</span>
                <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className={layout === 'dialog' ? 'z-[70] w-auto p-0' : 'w-auto p-0'}
              align="end"
            >
              <Calendar
                mode="range"
                defaultMonth={isoToLocalDate(dateRange.startDate)}
                selected={draftDateRange}
                onSelect={setDraftDateRange}
                numberOfMonths={2}
              />
              <div className="flex flex-col gap-2 border-t border-border px-3 py-3">
                {isDraftInvalid ? (
                  <p className="text-body-sm text-error">
                    End date must be on or after the start date.
                  </p>
                ) : !draftDateRange?.to ? (
                  <p className="text-body-sm text-foreground-muted">Select a start and end date.</p>
                ) : null}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setDatePickerOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleDateRangeApply}
                    disabled={!canApplyDateRange}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <label className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs">
            <span className="text-foreground-subtle">Granularity</span>
            <select
              value={granularity}
              onChange={(event) => setGranularity(event.target.value as PerformanceGranularity)}
              className="border-0 bg-transparent text-sm text-foreground outline-none"
              aria-label="Performance granularity"
            >
              {GRANULARITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div
            className="inline-flex overflow-hidden rounded-lg border border-border"
            role="group"
            aria-label="Performance view mode"
          >
            <button
              type="button"
              aria-pressed={viewMode === 'graph'}
              onClick={() => setViewMode('graph')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
                viewMode === 'graph'
                  ? 'bg-surface-muted text-foreground'
                  : 'bg-surface text-foreground-muted hover:bg-surface-muted/70'
              }`}
            >
              <BarChart3 className="h-3.5 w-3.5" aria-hidden />
              Graph
            </button>
            <button
              type="button"
              aria-pressed={viewMode === 'table'}
              onClick={() => setViewMode('table')}
              className={`inline-flex items-center gap-1.5 border-l border-border px-3 py-1.5 text-xs font-medium ${
                viewMode === 'table'
                  ? 'bg-surface-muted text-foreground'
                  : 'bg-surface text-foreground-muted hover:bg-surface-muted/70'
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
              Table
            </button>
          </div>

          <div className="relative" ref={metricsRef}>
            <button
              type="button"
              onClick={() => setMetricsOpen((open) => !open)}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-foreground hover:bg-surface-muted"
              aria-expanded={metricsOpen}
              aria-haspopup="listbox"
            >
              {visibleMetricIds.length} metrics
              <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
            </button>
            {metricsOpen ? (
              <div className="absolute right-0 top-[calc(100%+6px)] z-20 min-w-[180px] rounded-xl border border-border bg-surface p-2 shadow-lg">
                {PERFORMANCE_METRIC_DEFS.map((metric) => {
                  const selected = visibleMetricIds.includes(metric.id);
                  return (
                    <button
                      key={metric.id}
                      type="button"
                      onClick={() => toggleMetric(metric.id)}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-surface-muted"
                    >
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: metric.color }}
                        aria-hidden
                      />
                      <span className="flex-1">{metric.label}</span>
                      {selected ? <Check className="h-4 w-4 text-brand" aria-hidden /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-error/20 bg-error-subtle px-4 py-3 text-sm text-error-ink">
          <p>{error instanceof Error ? error.message : 'Failed to load performance data.'}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 font-semibold text-brand hover:underline"
          >
            Retry
          </button>
        </div>
      ) : null}

      {!error && loading && points.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-foreground-muted">
          <Loader2 className="h-5 w-5 animate-spin text-brand" aria-hidden />
          Loading performance data…
        </div>
      ) : null}

      {!error && !loading && !hasData ? (
        <p className="py-10 text-center text-sm text-foreground-subtle">
          No performance data for this period.
        </p>
      ) : null}

      {!error && hasData && viewMode === 'graph' ? (
        <PerformanceMetricsChart
          points={points}
          visibleMetricIds={visibleMetricIds}
          loading={loading}
          error={false}
        />
      ) : null}

      {!error && hasData && viewMode === 'table' ? (
        <PerformanceTableView points={points} visibleMetricIds={visibleMetricIds} />
      ) : null}
    </div>
  );
}
